<template>
  <div class="create-event">
    <h1 class="create-event__title page-title">
      {{ isEditing ? 'Edit event' : 'Create event' }}
    </h1>
    <div class="event-header__background">
      <event-image
        class="activity-preview__icon"
        :eventId="this.eventId"
        :activityId="formData.activityId == null ? activityId : formData.activityId"
        :key="formData.activityId"
        :alt="eventData.name"
        :isEditing="isEditing"
        @imageChanged="onImageChanged"/>
    </div>
    <div class="card">
      <el-form
        :model="formData"
        :rules="formRules"
        ref="newEventForm"
        class="create-event__form"
        label-position="top"
      >
        <div class="row">
          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="activityId"
              :error="errors.activityId"
              label="Activity"
            >
              <el-select
                class="wide"
                v-model="formData.activityId"
                @change="handleChangeActivity"
                :disabled="isEditing"
              >
                <el-option
                  v-for="type in descriptions.activityType"
                  :key="type.value"
                  :label="type.description"
                  :value="type.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="activityEventTypeId"
              :error="errors.activityEventTypeId"
              label="Type"
            >
              <el-select
                class="wide"
                v-model="formData.activityEventTypeId"
                :disabled="isEditing || !isActivityChosen"
              >
                <el-option
                  v-for="type in activityEventTypeDescriptions"
                  :key="type.value"
                  :label="type.description"
                  :value="type.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col col-xs-12">
            <el-form-item prop="link" :error="errors.link" label="Event URL">
              <el-input v-model="formData.link" />
            </el-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col col-xs-12 col-md-6">
            <el-form-item prop="name" :error="errors.name" label="Event name">
              <el-input v-model="formData.name" />
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="eventLevelTypeId"
              :error="errors.eventLevelTypeId"
              label="Level"
            >
              <el-select
                class="wide"
                v-model="formData.eventLevelTypeId"
                :disabled="!isActivityChosen"
              >
                <el-option
                  v-for="type in eventLevelTypeDescriptions"
                  :key="type.value"
                  :label="type.description"
                  :value="type.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="startDate"
              :error="errors.startDate"
              label="Start date"
            >
              <el-date-picker
                class="wide"
                v-model="formData.startDate"
                type="date"
                format="DD/MM/YYYY"
              />
            </el-form-item>
          </div>
          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="startDate"
              :error="errors.startTime"
              label="Start time"
            >
              <el-time-picker
                v-model="formData.startTime"
                arrow-control
                placeholder="Start time"
                format="HH:mm"
              />
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="endDate"
              :error="errors.endDate"
              label="End date"
            >
              <el-date-picker
                class="wide"
                v-model="formData.endDate"
                type="date"
                format="DD/MM/YYYY"
              />
            </el-form-item>
          </div>
          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="endTime"
              :error="errors.endTime"
              label="End time"
            >
            <el-time-picker
                v-model="formData.endTime"
                arrow-control
                placeholder="End time"
                format="HH:mm"
              />
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="isPrivate"
              :error="errors.isPrivate"
              label="Privacy"
            >
              <el-select class="wide" v-model="formData.isPrivate">
                <el-option label="Private" :value="true"></el-option>
                <el-option label="Public" :value="false"></el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="maxParticipants"
              :error="errors.maxParticipants"
              label="Maximum number of participants. 0 is no limit"
            >
              <el-input-number
                v-model="formData.maxParticipants"
                :min="0"
              ></el-input-number>
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              prop="location"
              :error="errors.location"
              label="Place"
            >
              <el-input v-model="formData.location" />
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              prop="details"
              :error="errors.details"
              label="Details"
            >
              <el-input v-model="formData.details" type="textarea" :rows="5" />
            </el-form-item>
          </div>
        </div>

        <div class="create-event__subtitle" v-if="!isEditing">
          Links to social networks
        </div>

        <div class="row" v-if="!isEditing">
          <div
            class="col col-xs-12"
            v-for="(social, index) in socialArray"
            :key="index"
          >
            <el-form label-position="top">
              <div class="row">
                <div class="col col-xs-11 col-md-4">
                  <el-form-item :error="social.networkIdError">
                    <el-select
                      class="wide"
                      v-model="social.socialNetworkId"
                      placeholder="Choose a social network"
                    >
                      <el-option
                        v-for="socialItem in socialDescriptions"
                        :key="socialItem.value"
                        :label="socialItem.description"
                        :value="socialItem.value"
                      >
                      </el-option>
                    </el-select>
                  </el-form-item>
                </div>

                <div
                  class="col col-xs-1 col-md-1 col-md-last col-lg-last col-xl-last col-xxl-last"
                >
                  <el-button
                    class="personal-data__remove-social-button"
                    type="primary"
                    icon="el-icon-delete"
                    @click="removeSocial(social)"
                  ></el-button>
                </div>

                <div class="col col-xs-11 col-md-7">
                  <el-form-item :error="social.accountError">
                    <el-input
                      v-model="social.accountLink"
                      placeholder="Link to event page in social network"
                    ></el-input>
                  </el-form-item>
                </div>
              </div>
            </el-form>
          </div>

          <div class="col col-xs-12">
            <el-button type="primary" @click="addSocial">
              <el-icon><Plus/></el-icon>
              Add social network
            </el-button>
          </div>
        </div>
      </el-form>
    </div>

    <div class="create-event__buttons">
      <el-button
        type="primary"
        @click="handleReset"
        :disabled="isButtonDisabled('save-event')"
      >
        {{ isEditing ? 'Cancel' : 'Reset' }}
      </el-button>
      <el-button
        type="primary"
        @click="validateForm"
        :disabled="isButtonDisabled('save-event')"
      >
        {{ isEditing ? 'Save' : 'Create' }}
      </el-button>
    </div>

    <edit-event-socials
      v-if="isEditing"
      :social-networks="oldData ? oldData.eventSocialNetworks : []"
      :event-id="eventId"
      @update-data="fetchEventData"
      ref="eventSocials"
    />
  </div>
</template>

<script>
import EditEventSocials from '@/components/events/EditEventSocials.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import EventImage from './EventImage.vue';
import { uploadImage} from '@/api/images.js'; 

import {
  getEvent,
  addEvent,
  updateEvent,
  getActivityEventTypeDescriptions,
  getEventLevelTypeDescriptions,
} from '@/api/events.js';
import { getSocialsTypeDescriptions } from '@/api/profile.js';
import {
  parseErrorsByFields,
  removeArrayElementById,
  removeTimezoneOffset,
  isTwoObjectsEqual,
} from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';
import { disableButton } from '@/mixins/disableButton.js';


export default {
  name: 'EventForm',
  mixins: [disableButton],
  components: {
    EditEventSocials,
    ConfirmationDialog,
    EventImage
  },

  data() {
    return {
      selectedFile: null,
      oldData: null,
      socialArray: [],
      activityEventTypeDescriptions: [],
      eventLevelTypeDescriptions: [],
      socialDescriptions: [],
      formData: {
        link:null,
        name: null,
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        details: null,
        location: null,
        maxParticipants: 0,
        isPrivate: true,
        activityId: null,
        activityEventTypeId: null,
        eventLevelTypeId: null,
      },
      errors: {
        link:null,
        name: null,
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        details: null,
        location: null,
        maxParticipants: null,
        isPrivate: null,
        activityId: null,
        activityEventTypeId: null,
        eventLevelTypeId: null,
      },
      formRules: {
        name: [
          {
            required: true,
            trigger: 'input',
            message: 'Please enter event name',
          },
        ],
        startDate: [
          {
            required: true,
            trigger: 'change',
            message: 'Please select a event start date',
          },
        ],
        link: [{ required: false }],
        startTime: [{ required: false }],
        endDate: [{ required: false }],
        endTime: [{ required: false }],
        details: [{ required: false }],
        location: [
          {
            required: true,
            trigger: 'input',
            message: 'Please enter event location',
          },
        ],
        maxParticipants: [{ required: false }],
        isPrivate: [
          {
            required: true,
            trigger: 'change',
            message: 'Please select event privacy',
          },
        ],
        activityId: [
          {
            required: true,
            trigger: 'change',
            message: 'Please select an activity',
          },
        ],
        activityEventTypeId: [
          {
            required: true,
            trigger: 'select',
            message: 'Please select event type',
          },
        ],
        eventLevelTypeId: [
          {
            required: true,
            trigger: 'select',
            message: 'Please select event level',
          },
        ],
      },
    };
  },
  computed: {
    isEditing() {
      return Boolean(this.$route.query.edit) || false;
    },
    eventId() {
      return Number(this.$route.query.eventId) || null;
    },
    teamId() {
      return Number(this.$route.query.teamId) || null;
    },
    activityId() {
      return Number(this.$route.query.activityId) || null;
    },
    eventData() {
      const { formData } = this;

      let data = {
        name: formData.name,
        link: formData.link,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        details: formData.details,
        location: formData.location,
        maxParticipants: formData.maxParticipants,
        isPrivate: formData.isPrivate,
        activityId: formData.activityId,
        activityEventTypeId: formData.activityEventTypeId,
        eventLevelTypeId: formData.eventLevelTypeId,
        eventSocialNetworks: null,
        teamId: this.teamId,
      };

      if (formData.startTime) {
        const startTime = new Date(formData.startTime);
        data.startDate.setHours(startTime.getHours());
        data.startDate.setMinutes(startTime.getMinutes());
      }

      if (formData.endTime) {
        const endTime = new Date(formData.endTime);
        data.endDate.setHours(endTime.getHours());
        data.endDate.setMinutes(endTime.getMinutes());
      }

      if (this.socialArray.length > 0) {
        data.eventSocialNetworks = this.socialArray.map((social) => {
          let accountLink = social.accountLink;
          if (social.accountLink.includes('/')) {
            const accounLinkSplited = social.accountLink.split('/');
            accountLink = accounLinkSplited[accounLinkSplited.length - 1];
          }
          return {
            accountLink,
            socialNetworkId: social.socialNetworkId,
          };
        });
      }

      return data;
    },
    descriptions() {
      return this.$store.state.descriptions;
    },
    isActivityChosen() {
      const { activityId } = this.formData;
      return activityId !== null;
    },
    isFormFilled() {
      const data = {
        name: this.formData.name,
        startDate: this.formData.startDate,
        isPrivate: this.formData.isPrivate,
        activityId: this.formData.activityId,
        activityEventTypeId: this.formData.activityEventTypeId,
        eventLevelTypeId: this.formData.eventLevelTypeId,
      };
      return Object.values(data).every((value) => value !== null);
    },
    isSocialsFilled() {
      const { socialArray } = this;

      return socialArray.every(
        (social) =>
          social.accountLink !== null && social.socialNetworkId !== null,
      );
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    removeArrayElementById,
    removeTimezoneOffset,
    isTwoObjectsEqual,
    async fetchEventData() {
      const { eventId } = this;
      const { data, success } = await getEvent({ eventId });

      if (success) {
        this.oldData = data;
        this.oldData.link = data.link == eventId ? null : data.link;
        this.setEventData();
      }
    },
    async fetchActivityEventTypeDescriptions() {
      const { activityId } = this.formData;
      const { data, success } = await getActivityEventTypeDescriptions({
        activityId,
      });

      if (success) {
        this.activityEventTypeDescriptions = data.items;
      }
    },
    async fetchEventLevelTypeDescriptions() {
      const { activityId } = this.formData;
      const { data, success } = await getEventLevelTypeDescriptions({
        activityId,
      });

      if (success) {
        this.eventLevelTypeDescriptions = data.items;
      }
    },
    async fetchSocialDescriptions() {
      const { data, success } = await getSocialsTypeDescriptions('Event');

      if (success) {
        this.socialDescriptions = data.items;
      }
    },
    async requestAddEvent() {
      this.disableButton('save-event');
      const { eventData } = this;
      const { data, success, errors } = await addEvent(eventData);
      this.enableButton('save-event');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Event has been successfully created',
        });
        this.$router.push({
          name: 'EventPage',
          params: {
            link: data.link,
          },
        });
      } else if (errors) {
        this.errors = { ...this.errors, ...parseErrorsByFields(errors) };
        if (
          errors.some((error) =>
            error.propertyName.includes('EventSocialNetworks'),
          )
        ) {
          this.setSocialErrors(errors);
        }
      }
    },
    async requestUpdateEvent() {
      this.disableButton('save-event');
      const { eventData, eventId } = this;

      const { success, errors } = await updateEvent({
        link: eventData.link,
        name: eventData.name,
        startDate: eventData.startDate,
        endDate: eventData.endDate,
        details: eventData.details,
        location: eventData.location,
        maxParticipants: eventData.maxParticipants,
        isPrivate: eventData.isPrivate,
        eventId: eventId,
        eventLevelTypeId: eventData.eventLevelTypeId,
      });
      this.enableButton('save-event');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Event has been successfully updated',
        });

        if( this.selectedFile != null) {
          await this.uploadEventImage();
        }

        this.$router.push({
          name: 'EventPage',
          params: {
            link: eventData.link || eventId,
          },
        });
      } else if (errors) {
        this.errors = { ...this.errors, ...parseErrorsByFields(errors) };
      }
    },

    async uploadEventImage() {
      try {
        // Use FormData for multipart data
        const formData = new FormData();

        const { eventId, selectedFile } = this;

        formData.append('entityId', eventId);
        formData.append('imageType', selectedFile.type);
        formData.append('image', selectedFile);

        //console.log(formData.get('imageType'));

        const response = await uploadImage(formData, '/api/Event/UploadEventPicture');

        this.selectedFile = null;

        // Handle successful upload
        console.log("Image uploaded successfully:", response.data);
      } catch (err) {
        console.error('Error uploading image:', err);
        this.error = "Failed to upload image. Please try again.";
      }
    },

    removeSocial(social) {
      const { socialArray, socialNetworks } = this;
      const socialIndex = socialArray.indexOf(social);
      this.socialArray = this.removeArrayElementById(socialArray, socialIndex);
    },
    clearSocialErrors() {
      this.socialArray.forEach((social) => {
        social.accountError = null;
        social.networkIdError = null;
      });
    },
    addSocial() {
      this.socialArray.push({
        accountLink: null,
        accountError: null,
        networkIdError: null,
        socialNetworkId: null,
      });
    },
    handleDescriptions() {
      const { isActivityChosen } = this;

      if (isActivityChosen) {
        this.fetchActivityEventTypeDescriptions();
        this.fetchEventLevelTypeDescriptions();
      }
    },
    handleChangeActivity() {
      this.formData.activityEventTypeId = null;
      this.formData.eventLevelTypeId = null;

      this.handleDescriptions();
    },
    validateForm() {
      const {
        isFormFilled,
        isEditing,
        isSocialsFilled,
        formData,
        oldData,
      } = this;

      if (this.$refs.newEventForm) {
        this.$refs.newEventForm.validate();
      }

      if (isEditing) {
        // сравнить старые данные и новые
        // если есть изменения, обновить

        const oldFormData = {
          link: oldData.link,
          name: oldData.name,
          startDate: oldData.startDate,
          startTime: new Date(oldData.startDate).getTime(),
          endDate: oldData.endDate,
          endTime: new Date(oldData.endDate).getTime(),
          details: oldData.details,
          location: oldData.location,
          maxParticipants: oldData.maxParticipants,
          isPrivate: oldData.isPrivate,
          activityId: this.descriptions.activityType.find(
            (type) => type.description === oldData.activityName,
          ).value,
          activityEventTypeId: this.activityEventTypeDescriptions.find(
            (type) => type.description === oldData.eventType,
          ).value,
          eventLevelTypeId: oldData.eventLevelType.eventLevelTypeId,
        };

        if (this.isTwoObjectsEqual(oldFormData, formData) && this.selectedFile == null) {
          this.showMessage({
            type: 'error',
            message: 'There are no changes to be saved',
          });
        } else {
          this.requestUpdateEvent();
        }
      } else if (isFormFilled && isSocialsFilled) {
        this.requestAddEvent();
      }
    },
    clearForm() {
      const { isEditing } = this;

      if (isEditing) {
        this.setEventData();
      } else {
        for (const key in this.formData) {
          const field = this.formData[key];
          if (key === 'maxParticipants') {
            field = 0;
          } else if (key === 'isPrivate') {
            field = true;
          } else {
            field = null;
          }
        }
      }
    },
    async setEventData() {
      const { oldData } = this;

      if (oldData) {
        this.formData.activityId = this.descriptions.activityType.find(
          (type) => type.description === oldData.activityName,
        ).value;

        await this.fetchActivityEventTypeDescriptions();
        await this.fetchEventLevelTypeDescriptions();

        this.formData.activityEventTypeId = this.activityEventTypeDescriptions.find(
          (type) => type.description === oldData.eventType,
        ).value;
        this.formData.eventLevelTypeId =
          oldData.eventLevelType.eventLevelTypeId;

        this.formData = {
          ...this.formData,
          link: oldData.link,
          name: oldData.name,
          startDate: oldData.startDate,
          startTime: new Date(oldData.startDate).getTime(),
          endDate: oldData.endDate,
          endTime: new Date(oldData.endDate).getTime(),
          details: oldData.details,
          location: oldData.location,
          maxParticipants: oldData.maxParticipants,
          isPrivate: oldData.isPrivate,
        };
      }
    },
    setSocialErrors(errors) {
      errors.forEach((error) => {
        if (error.propertyName.includes('EventSocialNetworks')) {
          const fieldName = error.propertyName.includes('AccountLink')
            ? 'accountError'
            : 'networkIdError';
          const socialIndex = error.propertyName.match(/\d+/);
          const message = error.errorMessages[0];

          this.socialArray[socialIndex][fieldName] = message;
        }
      });
    },
    handleReset() {
      const { isEditing, oldData, eventId } = this;
      if (isEditing) {
        this.$router.push({
          name: 'EventPage',
          params: {
            link: oldData.link || eventId,
          },
        });
      } else {
        this.clearForm();
      }
    },
    onImageChanged(selectedFile) {
      this.selectedFile = selectedFile;
    },
  },
  created() {
    this.fetchSocialDescriptions();

    const { isEditing } = this;

    if (isEditing) {
      this.fetchEventData();
    }
  },
};
</script>

<style lang="scss">
.create-event {
  &__title {
    margin: 0;
    margin-bottom: var(--global-dimension-size-250);

    font-weight: normal;
    font-size: var(--font-size-800);
    line-height: 1.6;
  }
  &__form {
  }
  &__subtitle {
    margin-top: var(--global-dimension-size-250);
    margin-bottom: var(--global-dimension-size-200);
    font-size: var(--font-size-200);
    line-height: 1.6;
  }
  &__buttons {
    margin-top: var(--global-dimension-size-350);
    margin-bottom: var(--global-dimension-size-350);
    text-align: right;
  }
}
</style>
